import { alpha, Box, type SxProps, type Theme } from "@mui/material";
import { forwardRef, type HTMLAttributes, type MouseEvent, type ReactNode } from "react";
import { ALPHA, GLYPH_BUTTON, INK, MOTION, SURFACE, VISUAL_CHOICE } from "@/theme/tokens";

export type GlyphButtonProps = Omit<HTMLAttributes<HTMLButtonElement>, "children" | "aria-label" | "onClick" | "type"> & {
	/** The glyph. Its shape is the whole message, so it carries no colour of its own — the button inks it. */
	icon: ReactNode;
	/** Already translated. The glyph has no visible text, so this is its only name. */
	ariaLabel: string;
	onClick: (event: MouseEvent<HTMLButtonElement>) => void;
	/** A small count in the top-trailing corner ("2 causes"). Absent = no badge. */
	badgeCount?: number;
	/** Held in its hover tint — while the card or sheet it opens is showing. */
	active?: boolean;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/**
 * A muted, icon-only button that ends a line of text: at rest a grey glyph that adds no weight to the line; on hover,
 * keyboard focus or while what it opens is showing, the brand tint. For "there is more about this here" on a dense row
 * — the origin glyph after a request's title. Forwards its ref and every other prop to the element, so it can be a
 * `HoverCard` trigger directly.
 */
export const GlyphButton = forwardRef<HTMLButtonElement, GlyphButtonProps>(function GlyphButton(
	{ icon, ariaLabel, onClick, badgeCount, active, dataTestId, sx, ...rest },
	ref,
) {
	return (
		<Box
			{...rest}
			ref={ref}
			component="button"
			type="button"
			aria-label={ariaLabel}
			data-testid={dataTestId}
			data-active={active === true}
			onClick={onClick}
			sx={[
				(theme) => {
					const hot = { color: theme.palette.primary.dark, backgroundColor: alpha(theme.palette.primary.main, ALPHA.CONTROL_TINT_HOVER) };
					return {
						position: "relative",
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						flexShrink: 0,
						width: GLYPH_BUTTON.SIZE_PX,
						height: GLYPH_BUTTON.SIZE_PX,
						p: 0,
						border: "none",
						borderRadius: 1,
						background: "none",
						color: INK.MUTED,
						cursor: "pointer",
						font: "inherit",
						verticalAlign: "middle",
						"& > svg": { fontSize: GLYPH_BUTTON.GLYPH },
						transition: `color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}, background-color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}, transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
						"@media (hover: hover) and (pointer: fine)": { "&:hover": hot },
						"&:focus-visible": { ...hot, outline: "none", boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, ALPHA.FOCUS_RING)}` },
						"&[data-active='true']": { ...hot, boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, ALPHA.FOCUS_RING)}` },
						"&:active": { transform: `scale(${VISUAL_CHOICE.PRESS_SCALE})` },
						"@media (prefers-reduced-motion: reduce)": { transition: "none", "&:active": { transform: "none" } },
					};
				},
				...(sx === undefined ? [] : Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{icon}
			{badgeCount !== undefined && (
				<Box
					component="span"
					aria-hidden
					sx={(theme) => ({
						position: "absolute",
						top: GLYPH_BUTTON.BADGE_OFFSET_PX,
						insetInlineEnd: GLYPH_BUTTON.BADGE_OFFSET_PX,
						minWidth: GLYPH_BUTTON.BADGE_SIZE_PX,
						height: GLYPH_BUTTON.BADGE_SIZE_PX,
						px: 0.375,
						boxSizing: "border-box",
						display: "grid",
						placeItems: "center",
						borderRadius: `${GLYPH_BUTTON.BADGE_SIZE_PX}px`,
						border: `${GLYPH_BUTTON.BADGE_RING_PX}px solid ${SURFACE}`,
						backgroundColor: theme.palette.primary.dark,
						color: theme.palette.primary.contrastText,
						fontSize: GLYPH_BUTTON.BADGE_FONT,
						fontWeight: 700,
						lineHeight: 1,
						fontVariantNumeric: "tabular-nums",
					})}
				>
					{badgeCount}
				</Box>
			)}
		</Box>
	);
});
